import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2, ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {Paginator} from 'primeng/paginator';
import {PaginatorLoadData} from '../../models/paginator.model';
import {LoaderService} from '../../services/loader.service';
import {DispatcherService} from '../../services/dispatcher.service';

@Component({
  selector: 'si-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PaginatorComponent implements OnInit, OnDestroy {
  @Input() id: string;
  @Input() path: string;
  @Input() rows = 20;
  @Input() rowsPerPageOptions = [5, 10, 20, 50, 75, 100];
  @Input() items: any[];
  @Input() totalRecords: number;
  @Input() rowsText = 'CORE_ROWS';
  @Input() loadOnInit = true;
  @Output() reloadChange = new EventEmitter<number>();
  @Output() itemsChange = new EventEmitter<any[]>();
  @Output() totalRecordsChange = new EventEmitter<number>();
  @Output() pageChange = new EventEmitter<number>();
  @ViewChild('pPaginator', { static: true }) pPaginator: Paginator;
  lastLoadData: PaginatorLoadData;
  reloadItemsSubscription: number;

  // Indicates if there is user activity or not.
  activeDom = true;

  private paginationInfoElement: HTMLElement;

  // If the reset variable is initialized with 0, the component does NOT get the items for the first time. Otherwise, it DOES.
  private _reset: number;
  private _reload: number;
  private _reloadInterval: number;

  constructor(private http: HttpClient,
              private loaderService: LoaderService,
              private renderer: Renderer2,
              private translateService: TranslateService,
              private dispatcher: DispatcherService,
              private element: ElementRef) {
  }

  get reset() {
    return this._reset;
  }

  @Input()
  set reset(val) {
    this._reset = val;
    if (val) {
      setTimeout(() => {
        if(this.loadOnInit || val > 0){ //avoid two calls on load when the page has default filters that increments the resetPaginator
          this.lastLoadData = null;
          this.getItems();
          this.pPaginator.first = 0;
        }
        this.loadOnInit = true;
      });
    }
  }

  get reload() {
    return this._reload;
  }

  @Input()
  set reload(val) {
    this._reload = val;
    if (val) {
      this.reloadItems();
    }
  }

  get reloadInterval() {
    return this._reloadInterval;
  }

  @Input()
  set reloadInterval(val) {
    this._reloadInterval = val;
    if (val) {
      this.reloadItemsSubscription = <any>setInterval(() => this.reloadItems(true), val);
    }
  }

  ngOnInit() {
    // If there are more than one paginator in the same page, they need an id to be differentiated.
    const observerKey = 'paginatorComponent' + this.id;
    this.dispatcher.register('idle-time-inactivity', () => this.onActivateDOM(false), observerKey);
    this.dispatcher.register('idle-time-activity', () => this.onActivateDOM(true), observerKey);
  }

  onActivateDOM(activateFlag = true): void {
    this.activeDom = activateFlag;
  }

  async onPageChange({first, rows, page, pageCount}: PaginatorLoadData, isReloading = false): Promise<void> {
    const rowsParsed = parseInt(rows.toString(), 10);
    this.lastLoadData = {first, rows: rowsParsed, page, pageCount};
    await this.getItems(page, null, first, rowsParsed);
  }

  async getItems(page = 0, size?: string, first = 0, rows?: number): Promise<void> {
    this.loaderService.start();
    const httpParams = new HttpParams()
      .set('page', page.toString())
      .set('size', (size || this.pPaginator.rows).toString());

    await this.http.get(this.path, {params: httpParams, observe: 'response'}).toPromise().then(response => {
      this.items = <any[]>response.body;
      this.itemsChange.emit(this.items);
      this.totalRecords = parseInt(response.headers.get('x-total-count'), 10);
      this.totalRecordsChange.emit(this.totalRecords);
      this.pageChange.emit(page);
    }, error => {
      this.loaderService.stop();
    });

    await this.setPaginationInfo(this.totalRecords, first, rows || this.rows);
    this.loaderService.stop();
  }

  async setPaginationInfo(totalRows: number, originalFirst: number, rows: number): Promise<void> {
    const ofText = await this.translateService.get('CORE_OF').toPromise();
    const firstRow = originalFirst + 1;
    let lastRow = originalFirst + rows;
    lastRow = lastRow < totalRows ? lastRow : totalRows;

    if (!this.paginationInfoElement) {
      const siPaginatorElement = this.element.nativeElement;
      const uiPaginatorElement = siPaginatorElement.querySelector('.p-paginator');
      const uiDropdownElement = uiPaginatorElement.querySelector('.p-dropdown');

      // Translate texts.
      const rowsText = await this.translateService.get(this.rowsText).toPromise();

      // Create rows text.
      const rowsTextElement = this.renderer.createElement('div');
      const paginationRowsTextElement = this.renderer.createText(rowsText);
      rowsTextElement.appendChild(paginationRowsTextElement);
      this.renderer.insertBefore(uiDropdownElement.parentElement, rowsTextElement, uiDropdownElement);

      // Create pagination info container.
      this.paginationInfoElement = this.renderer.createElement('div');
      this.renderer.appendChild(uiDropdownElement.parentElement, this.paginationInfoElement);
    } else {
      this.renderer.removeChild(this.paginationInfoElement, this.paginationInfoElement.firstChild);
    }

    const paginationInfoText = `${firstRow}-${lastRow} ${ofText} ${totalRows}`;
    const paginationInfoTextElement = this.renderer.createText(paginationInfoText);
    this.paginationInfoElement.appendChild(paginationInfoTextElement);
  }

  async reloadItems(intervalReloading?: boolean): Promise<void> {
    if (intervalReloading && this.activeDom) {
      return;
    }

    if (this.lastLoadData) {
      await this.onPageChange(this.lastLoadData, true);
    } else {
      await this.getItems();
    }

    this.reloadChange.emit(this.reload);
  }

  ngOnDestroy() {
    const observerKey = 'paginatorComponent' + this.id;
    this.dispatcher.unregisterFunction('idle-time-inactivity', observerKey);
    this.dispatcher.unregisterFunction('idle-time-activity', observerKey);
    if (this.reloadItemsSubscription) {
      clearInterval(this.reloadItemsSubscription);
    }
  }
}
